import React, { useMemo, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogContent,
  DialogBody,
  DialogSurface,
  DialogTitle,
  Button,
  mergeClasses,
  Body1,
  Subtitle1,
  Caption1,
  Label,
} from '@fluentui/react-components';
import { Dismiss20Regular } from '@fluentui/react-icons';
import { UrlInputFluent } from '@bingads-webui-campaign-react/url-input-fluent-v2';
import { TemplateDetailsCarousel } from '../template-details-carousel';
import { useTemplateDetailsModalStyles } from './style';


export const TemplateDetailsModal = ({
  templateGroupId,
  templateGroupName,
  fetchDisplayAdsTemplateGroupDetails,
  handleFailedToLoadData,
  show,
  onShowChange,
  onSelectTemplate,
  onClose,
  i18n,
  logger,
}) => {
  const [templateCount, setTemplateCount] = useState(0);
  const [templateImages, setTemplateImages] = useState([]);
  const [templateGroupDetails, setTemplateGroupDetails] = useState({});
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [url, setUrl] = useState('');
  const [urlError, setUrlError] = useState('');

  useEffect(() => {
    fetchDisplayAdsTemplateGroupDetails([templateGroupId])
      .then(({ data }) => {
        const details = data.details.find(detail => detail.templateGroupId === templateGroupId);
        setTemplateGroupDetails(details);
        setTemplateImages(details.displayAdsTemplateDetails.map(template => template.displayAd.url));
        setTemplateCount(details.templateCount);
      })
      .catch(() => {
        handleFailedToLoadData();
        logger.logTrace('failed to load');
      })
      .finally(() => {
        setIsPageLoading(false);
      });
  }, [fetchDisplayAdsTemplateGroupDetails, handleFailedToLoadData, logger, templateGroupId]);


  const onGetStartedButton = useCallback(() => {
    onSelectTemplate({ templateGroupId, templateGroupDetails, url: urlError ? '' : url });
  }, [onSelectTemplate, templateGroupId, templateGroupDetails, url, urlError]);

  const DismissIcon = useMemo(() => <Dismiss20Regular onClick={onClose} />, [onClose]);

  const classes = useTemplateDetailsModalStyles();

  return (
    <Dialog
      open={show}
      onDismiss={onClose}
      onOpenChange={(event, data) => {
        // it is the users responsibility to react accordingly to the open state change
        onShowChange(data.open);
      }}
      className={mergeClasses('template-details-dialog', classes.dialog)}
    >
      <DialogSurface className={mergeClasses('template-details-modal', classes.dialog)}>
        <DialogBody>
          <DialogTitle action={DismissIcon} />
          <DialogContent>
            <div className={classes.dialogContentContainer}>
              <div className={classes.carouselContainer}>
                <TemplateDetailsCarousel loading={isPageLoading} previews={templateImages} i18n={i18n} />
              </div>
              <div className={classes.detailsContainer}>
                <Subtitle1> {templateGroupName} </Subtitle1>
                <div className={classes.textGroup}>
                  <Body1> {i18n.getString(_TL_('{{count}} sizes'), { count: templateCount })} </Body1>
                  <Body1> {i18n.getString(_TL_('All {{count}} sizes will be automatically added to your template for you to customize.'), { count: templateCount })} </Body1>
                </div>
                <div className={classes.textGroup}>
                  <Label weight="semibold"> {i18n.getString(_TL_('Enter a URL and we’ll use it to generate relevant assets for your template.'))} </Label>
                  <UrlInputFluent
                    url={url}
                    onChange={(urlErrors, newUrl) => {
                      setUrlError(urlErrors[0]);
                      setUrl(newUrl);
                    }}
                    textFieldAriaLabel={i18n.getString(_TL_('Enter your URL'))}
                    i18n={i18n}
                    additionalProperties={{
                      placeholder: i18n.getString(_TL_('Enter your URL')),
                      id: 'url-input',
                    }}
                  />
                  {urlError && <Caption1 className={classes.errorMsg}> {urlError} </Caption1>}
                </div>
                <Button onClick={onGetStartedButton} disabled={templateImages.length === 0} appearance="primary" className="get-started-button">
                  {i18n.getString(_TL_('Get started'))}
                </Button>
              </div>
            </div>
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};


TemplateDetailsModal.propTypes = {
  templateGroupId: PropTypes.string.isRequired,
  templateGroupName: PropTypes.string.isRequired,
  fetchDisplayAdsTemplateGroupDetails: PropTypes.func.isRequired,
  handleFailedToLoadData: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
  onShowChange: PropTypes.func.isRequired,
  onSelectTemplate: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
};
